import { PointAndPlaneFragment } from '@warebee/frontend/data-access-api-graphql';
import {
  ActualityHqDataColumn,
  ActualityHqDataRows,
  getActualityAggregatedHqDataRowsQuery,
  getActualityHqDataRowsQuery,
} from '@warebee/shared/dataset-queries';
import _ from 'lodash';
import { atom, selector, selectorFamily } from 'recoil';
import { getSequentialColor } from '../../common/color.helper';
import {
  getHeatmapLinearRangeBuckets,
  HEATMAP_BUCKETS_COUNT,
} from '../../common/heatmap.helper';
import { persistAtom } from '../../common/recoil/persistAtom';
import { viewerSelectedPlane } from '../../layout/viewer/store/viewer.state';
import { sidebarStateByType } from '../../store/sidebar.state';
import { userSettings, warehouseSelectedId } from '../../store/warehouse.state';
import { actualityAggregatedMetricsDefault } from './actuality.default';
import { actualityHeatmapRequestReadyInner } from './actuality.heatmap.state';
import { getHqPreset } from './actuality.helper';
import { actualityHqBuilderParams } from './actuality.hq.state';
import { getActualityAggregatedHqExtendedDataRows } from './actuality.kpi.helper';
import {
  ActualityAggregatedHqExtendedDataRow,
  ActualityAggregatedHqExtendedStatsDataColumn,
  ActualityAggregatedStatsGroupSelectionMode,
  ActualityAggregatedStatsSortMode,
  ActualityAggregationField,
} from './actuality.kpi.types';
import {
  actualityQueryReuseHashSources,
  actualityResourcePolicy,
} from './actuality.state';
import { executeDatasetQuery } from './feed.helper';
import { feedAgentShifts } from './feed.state';

const getKey = (postfix: string) =>
  `warebee-actuality-aggregated-stats-${postfix}`;

const actualityAggregatedStatsSelectedGroupsAtom = atom<
  Partial<ActualityAggregatedHqExtendedDataRow>[]
>({
  key: getKey('selected-groups-atom'),
  default: [],
});

export const actualityAggregatedStatsSelectedGroups = selector<
  Partial<ActualityAggregatedHqExtendedDataRow>[]
>({
  key: getKey('selected-groups'),
  get: ({ get }) => {
    const mode = get(actualityAggregatedStatsGroupSelectionMode);
    const groups = get(actualityAggregatedStatsSelectedGroupsAtom);
    return mode === 'single' ? _.takeRight(groups, 1) : groups;
  },
  set: (
    { get, set },
    value: Partial<ActualityAggregatedHqExtendedDataRow>[],
  ) => {
    const mode = get(actualityAggregatedStatsGroupSelectionMode);
    const groupBy = get(actualityAggregatedStatsGroupBy);
    let valuesToSet: Partial<ActualityAggregatedHqExtendedDataRow>[] = value;
    if (mode === 'single') {
      valuesToSet = _.takeRight(value, 1);
    }

    set(actualityAggregatedStatsSelectedSubGroup, current => {
      const hasGroupSelected = _.some(
        valuesToSet,
        group => group[groupBy] === current?.[groupBy],
      );
      return hasGroupSelected ? current : null;
    });

    set(actualityAggregatedStatsSelectedGroupsAtom, valuesToSet);
  },
});

export const actualityAggregatedStatsGroupSelectionMode =
  atom<ActualityAggregatedStatsGroupSelectionMode>({
    key: getKey('group-selection-mode'),
    default: 'single',
  });

export const actualityAggregatedStatsGroupBy =
  persistAtom<ActualityAggregationField>({
    key: getKey('group-by'),
    default: 'agentId',
  });

export const actualityAggregatedStatsGroupBySecondary =
  persistAtom<ActualityAggregationField>({
    key: getKey('aggregate-by'),
    default: 'jobId',
  });

export const actualityAggregatedStatsSelectedSubGroupAtom = atom<
  Partial<ActualityAggregatedHqExtendedDataRow>
>({
  key: getKey('selected-sub-group-atom'),
  default: null,
});

export const actualityAggregatedStatsSelectedSubGroup = selector<
  Partial<ActualityAggregatedHqExtendedDataRow>
>({
  key: getKey('selected-sub-group'),
  get: ({ get }) => get(actualityAggregatedStatsSelectedSubGroupAtom),
  set: ({ get, set }, value) => {
    set(sidebarStateByType('sidebar-feed-level-3'), current => ({
      ...current,
      isCollapsed: _.isNil(value),
      isHidden: _.isNil(value),
    }));

    set(actualityAggregatedStatsSelectedSubGroupAtom, value);
  },
});

const actualityAggregatedStatsSelectedAggregatedMetricAtom =
  atom<ActualityAggregatedHqExtendedStatsDataColumn>({
    key: getKey('aggregated-metric-atom'),
    default: 'eventsCount',
  });

export const actualityAggregatedStatsSelectedAggregatedMetric =
  selector<ActualityAggregatedHqExtendedStatsDataColumn>({
    key: getKey('aggregated-metric-'),
    get: ({ get }) => {
      const available = get(actualityAggregatedStatsUserSelectedMetrics);
      const current = get(actualityAggregatedStatsSelectedAggregatedMetricAtom);
      if (_.some(available, m => m === current)) {
        return current;
      } else return _.head(available);
    },
    set: ({ set }, value) =>
      set(actualityAggregatedStatsSelectedAggregatedMetricAtom, value),
  });

export const actualityAggregatedStatsGroupsSortMode =
  atom<ActualityAggregatedStatsSortMode>({
    key: getKey('group-sort-mode'),
    default: 'metricValue',
  });

export const actualityAggregatedStatsGroupsSortByDirection = atom<
  'asc' | 'desc'
>({
  key: getKey('group-sort-direction'),
  default: 'desc',
});

export const actualityAggregatedStatsSubGroupsSortMode =
  atom<ActualityAggregatedStatsSortMode>({
    key: getKey('subgroup-sort-mode'),
    default: 'metricValue',
  });

export const actualityAggregatedStatsSubGroupsSortByDirection = atom<
  'asc' | 'desc'
>({
  key: getKey('subgroup-sort-direction'),
  default: 'desc',
});

export const actualityAggregatedStatsViewMode = persistAtom<
  'summary' | 'details'
>({
  key: getKey('view-mode'),
  default: 'details',
});

export const actualityAggregatedStatsSearchTerm = atom<string>({
  key: getKey('search-term'),
  default: '',
});

export const actualityAggregatedStatsTotal =
  selector<ActualityAggregatedHqExtendedDataRow>({
    key: getKey('total'),
    get: async ({ get }) => {
      const warehouseId = get(warehouseSelectedId);
      const shifts = get(feedAgentShifts);
      const rp = get(actualityResourcePolicy);

      const compiledQuery = getActualityAggregatedHqDataRowsQuery({
        ...get(actualityHqBuilderParams),
        groupBy: [],
      }).compile();

      const [rows] = await executeDatasetQuery({
        warehouseId,
        compiledQuery,
        comment: `[actuality] Aggregated Stats Totals`,
        reuseHashSource: get(actualityQueryReuseHashSources),
      });

      const extended = getActualityAggregatedHqExtendedDataRows({
        rows,
        agents: rp.agents,
        shifts,
      });
      return _.head(extended);
    },
  });

export const actualityAggregatedStatsByGroups = selectorFamily<
  ActualityAggregatedHqExtendedDataRow[],
  ActualityHqDataColumn[]
>({
  key: getKey('by-groups'),
  get:
    (groupBy: ActualityHqDataColumn[]) =>
    async ({ get }) => {
      const warehouseId = get(warehouseSelectedId);
      const shifts = get(feedAgentShifts);
      const rp = get(actualityResourcePolicy);

      const compiledQuery = getActualityAggregatedHqDataRowsQuery({
        ...get(actualityHqBuilderParams),
        groupBy,
      }).compile();

      const [rows] = await executeDatasetQuery({
        warehouseId,
        compiledQuery,
        comment: `[actuality] Aggregated Stats By: ${_.join(groupBy, ' ,')}`,
        reuseHashSource: get(actualityQueryReuseHashSources),
      });

      const extendedRows = getActualityAggregatedHqExtendedDataRows({
        rows,
        agents: rp.agents,
        shifts,
      });
      return extendedRows;
    },
});

export const actualityAggregatedStatsBySelectedGroup = selector<
  ActualityAggregatedHqExtendedDataRow[]
>({
  key: getKey('by-selected-group'),
  get: async ({ get }) => {
    const groupBy = get(actualityAggregatedStatsGroupBy);
    const metric = get(actualityAggregatedStatsSelectedAggregatedMetric);
    const sortMode = get(actualityAggregatedStatsGroupsSortMode);
    const sortDirection = get(actualityAggregatedStatsGroupsSortByDirection);
    const extendedRows = await get(actualityAggregatedStatsByGroups([groupBy]));

    const orderByField =
      sortMode === 'groupTitle'
        ? groupBy
        : sortMode === 'minTime'
          ? 'minEventTime'
          : metric;

    const sorted = _.orderBy(
      extendedRows,
      row => _.get(row, orderByField) ?? 0,
      [sortDirection],
    );
    return sorted;
  },
});

export const actualityAggregatedStatsGroupIdsSorted = selector<any[]>({
  key: getKey('stats-group-ids-sorted'),
  get: async ({ get }) => {
    const stats = get(actualityAggregatedStatsBySelectedGroup);
    const groupBy = get(actualityAggregatedStatsGroupBy);
    return _(stats)
      .map(row => _.get(row, groupBy))
      .uniq()
      .value();
  },
});

export const actualityAggregatedStatsByGroupAndTimeData = selector<
  ActualityAggregatedHqExtendedDataRow[]
>({
  key: getKey('by-group-and-time-data'),
  get: async ({ get }) => {
    const warehouseId = get(warehouseSelectedId);
    const groupBy = get(actualityAggregatedStatsGroupBy);
    const shifts = get(feedAgentShifts);
    const rp = get(actualityResourcePolicy);
    const timeField = get(actualityAggregatedStatsGroupBySecondary);

    const compiledQuery = getActualityAggregatedHqDataRowsQuery({
      ...get(actualityHqBuilderParams),
      groupBy: [groupBy, timeField],
    }).compile();

    const [rows] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: `[actuality] Aggregated Stats Timeline By:  ${groupBy}, ${timeField}`,
      reuseHashSource: get(actualityQueryReuseHashSources),
    });

    return getActualityAggregatedHqExtendedDataRows({
      rows,
      agents: rp.agents,
      shifts,
    });
  },
});

export const actualityAggregatedStatsByGroupAndTime = selector<
  ActualityAggregatedHqExtendedDataRow[]
>({
  key: getKey('by-group-and-time'),
  get: async ({ get }) => {
    const groupBySecondary = get(actualityAggregatedStatsGroupBySecondary);
    const metric = get(actualityAggregatedStatsSelectedAggregatedMetric);
    const sortMode = get(actualityAggregatedStatsSubGroupsSortMode);
    const sortDirection = get(actualityAggregatedStatsSubGroupsSortByDirection);
    const extendedRows = await get(actualityAggregatedStatsByGroupAndTimeData);

    const orderByField =
      sortMode === 'groupTitle'
        ? groupBySecondary
        : sortMode === 'minTime'
          ? 'minEventTime'
          : metric;

    const sorted = _.orderBy(
      extendedRows,
      row => _.get(row, orderByField) ?? 0,
      [sortDirection],
    );
    return sorted;
  },
});

export const actualityAggregatedStatsMinMetricValue = selector<number>({
  key: getKey('min-metric-value'),
  get: ({ get }) => {
    const metric = get(actualityAggregatedStatsSelectedAggregatedMetric);
    const aggregatedStats = get(actualityAggregatedStatsByGroupAndTime);
    return _.minBy(aggregatedStats, metric)?.[metric] ?? (0 as any as number);
  },
});

export const actualityAggregatedStatsMaxMetricValue = selector<number>({
  key: getKey('max-metric-value'),
  get: ({ get }) => {
    const metric = get(actualityAggregatedStatsSelectedAggregatedMetric);
    const aggregatedStats = get(actualityAggregatedStatsByGroupAndTime);
    return _.maxBy(aggregatedStats, metric)?.[metric] ?? (0 as any as number);
  },
});

export const actualityAggregatedStatsBuckets = selector({
  key: getKey('buckets'),
  get: ({ get }) => {
    const minValue = get(actualityAggregatedStatsMinMetricValue);
    const maxValue = get(actualityAggregatedStatsMaxMetricValue);

    const buckets = getHeatmapLinearRangeBuckets({
      minValue,
      maxValue,
      bucketCount: HEATMAP_BUCKETS_COUNT,

      getColor: bucketNum =>
        getSequentialColor(
          (100 * bucketNum) / HEATMAP_BUCKETS_COUNT,
          'velocity-analytics',
        ),
    });
    return buckets;
  },
});

export const actualityAggregatedStatsSelection = atom<
  Partial<ActualityAggregatedHqExtendedDataRow>
>({
  key: getKey('selection'),
  default: {},
});

export const actualityProcessStats = selector<
  ActualityAggregatedHqExtendedDataRow[]
>({
  key: getKey('by-process'),
  get: async ({ get }) => {
    const warehouseId = get(warehouseSelectedId);
    const shifts = get(feedAgentShifts);
    const rp = get(actualityResourcePolicy);

    const compiledQuery = getActualityAggregatedHqDataRowsQuery({
      ...get(actualityHqBuilderParams),
      groupBy: ['eventProcessType'],
    }).compile();

    const [rows] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: `[actuality] Process Stats`,
      reuseHashSource: get(actualityQueryReuseHashSources),
    });

    const extendedRows = getActualityAggregatedHqExtendedDataRows({
      rows,
      agents: rp.agents,
      shifts,
    });
    return extendedRows;
  },
});

export const actualityAggregatedScopedEvents = selectorFamily<
  ActualityHqDataRows,
  Partial<ActualityAggregatedHqExtendedDataRow>
>({
  key: getKey('scoped-events'),
  get:
    (subgroupKey: Partial<ActualityAggregatedHqExtendedDataRow>) =>
    async ({ get }) => {
      const warehouseId = get(warehouseSelectedId);
      if (!get(actualityHeatmapRequestReadyInner)) return null;
      if (_.isNil(subgroupKey)) return null;
      const queryParamsBase = get(actualityHqBuilderParams);
      const scopePreset = getHqPreset(subgroupKey);
      const queryParams = {
        ...queryParamsBase,
        filterPresets: [...queryParamsBase.filterPresets, scopePreset],
        page: {
          skip: 0,
          limit: 1000,
        },
      };
      const compiledQuery = getActualityHqDataRowsQuery(queryParams).compile();

      const [result] = await executeDatasetQuery({
        warehouseId,
        compiledQuery,
        comment: '[actuality] Scoped Events',
        reuseHashSource: get(actualityQueryReuseHashSources),
      });

      return result;
    },
});

export const actualityAggregatedScopedEventsRoute = selectorFamily<
  PointAndPlaneFragment[][],
  Partial<ActualityAggregatedHqExtendedDataRow>
>({
  key: getKey('scoped-events-routes'),
  get:
    (subgroupKey: Partial<ActualityAggregatedHqExtendedDataRow>) =>
    ({ get }) => {
      const plane = get(viewerSelectedPlane);
      if (_.isNil(subgroupKey)) return [];
      const events = get(actualityAggregatedScopedEvents(subgroupKey));
      const points = _.map(events, e =>
        _.flatten(e.route as any as PointAndPlaneFragment[][]),
      );
      return points;
    },
});

export const actualityAggregatedScopedEventLocations = selectorFamily<
  Set<String>,
  Partial<ActualityAggregatedHqExtendedDataRow>
>({
  key: getKey('scoped-events-locations'),
  get:
    (subgroupKey: Partial<ActualityAggregatedHqExtendedDataRow>) =>
    ({ get }) => {
      const plane = get(viewerSelectedPlane);
      if (_.isNil(subgroupKey)) return new Set();
      const events = get(actualityAggregatedScopedEvents(subgroupKey));
      const locations = _(events)
        .map(e => e.layoutLocationId)
        .uniq()
        .value();
      return new Set(locations);
    },
});

export const actualityAggregatedScopedEventLocationsSelected = selector<
  Set<String>
>({
  key: getKey('scoped-events-locations-selected'),
  get: ({ get }) => {
    const subgroup = get(actualityAggregatedStatsSelectedSubGroup);
    if (_.isNil(subgroup)) return new Set();

    return get(actualityAggregatedScopedEventLocations(subgroup));
  },
});

export const actualityAggregatedStatsUserSelectedMetrics = selector<
  ActualityAggregatedHqExtendedStatsDataColumn[]
>({
  key: getKey('user-selected-metrics'),
  get: ({ get }) => {
    return (
      get(userSettings)?.extraSettings?.actualitySettings?.aggregatedMetrics ??
      actualityAggregatedMetricsDefault
    );
  },
});
